import { css } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  IconButton,
  Theme,
  useTheme,
} from "@mui/material";
import { MouseEventHandler } from "react";
import { match } from "ts-pattern";

import Loading from "~/components/me/bento/Loading";
import RetriableError from "~/components/me/bento/RetriableError";
import ContentBodyLayout from "~/components/me/bento/content/ContentBodyLayout";
import DialogBody from "~/components/me/bento/content/point-report/DialogBody";
import { usePointReportContent } from "~/components/me/bento/lib";
import { useOpenContentModal } from "~/components/me/hooks";
import { ContentName } from "~/components/me/lib";
import {
  fixedHeight,
  fixedWidth,
  flexColumn,
  flexRow,
} from "~/lib/client/design";
import useIsSpMode, { SP_MODE_WIDTH } from "~/lib/client/hooks/useIsSpMode";
import { MePointReportQuery, Sdk } from "~/lib/graphql/client";
import "~/lib/monkeypatch";

const contentName: ContentName = "PointReport";

const PointReport = ({ client }: { client: Sdk }) => {
  const result = usePointReportContent(client);
  const theme = useTheme();
  const isSpMode = useIsSpMode();

  const { openedContent, openContentModal, closeContentModal } =
    useOpenContentModal();

  const isOpen = openedContent === contentName && result.status === "success";
  const openModal = () => {
    if (result.status !== "success") return;
    openContentModal(contentName);
  };
  const closeModal = () => closeContentModal();

  return (
    <>
      {match(result)
        .with({ status: "pending" }, () => <Loading />)
        .with({ status: "error" }, (matched) => (
          <RetriableError reload={matched.refetch} />
        ))
        .with({ status: "success" }, (matched) => (
          <Body content={matched.data} onClick={openModal} />
        ))
        .exhaustive()}
      {result.status === "success" && (
        <Dialog
          open={isOpen}
          onClose={closeModal}
          fullWidth
          PaperProps={{
            sx: {
              width: "100%",
              height: "80vh",
              m: 1,
            },
          }}
          maxWidth={"lg"}
        >
          <DialogContent
            sx={{
              m: isSpMode ? -2 : 0,
            }}
          >
            <IconButton onClick={closeModal}>
              <CloseIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
            <DialogBody content={result.data} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const Body = ({
  content,
  onClick,
}: {
  content: MePointReportQuery;
  onClick: MouseEventHandler;
}) => {
  const theme = useTheme();
  const isSpMode = useIsSpMode();

  const fussyPoints = content.me.fussyPoints
    .sort(
      (a, b) =>
        b.creatorPoint +
        b.curatorPoint +
        b.expertPoint +
        b.evangelistPoint +
        b.supporterPoint -
        (a.creatorPoint +
          a.curatorPoint +
          a.expertPoint +
          a.evangelistPoint +
          a.supporterPoint)
    )
    .slice(0, isSpMode ? 3 : 4);

  return (
    <ContentBodyLayout onClick={onClick} label={"Point Report"}>
      <div css={categoriesContainer}>
        {fussyPoints.map((fussyPoint, index) => (
          <div key={index} css={categoryPointPanelStyle}>
            <div css={rankAndPointStyle}>
              <span css={rankLabelStyle(theme)}>{(index + 1).ordinal()}</span>
              <span css={pointStyle}>
                {(
                  fussyPoint.creatorPoint +
                  fussyPoint.curatorPoint +
                  fussyPoint.expertPoint +
                  fussyPoint.evangelistPoint +
                  fussyPoint.supporterPoint
                ).toLocaleString()}
              </span>
            </div>
            <span css={categoryNameStyle}>{fussyPoint.category.name}</span>
          </div>
        ))}
      </div>
    </ContentBodyLayout>
  );
};

const categoriesContainer = css`
  ${flexColumn};
  gap: 2px;
  margin-top: 0.5rem;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    margin-top: 0.25rem;
  }
`;

const categoryPointPanelStyle = css`
  ${fixedWidth("220px")}
  ${fixedHeight("44px")}

  ${flexRow};
  align-items: center;

  border-radius: 1rem;
  background: #fff;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    ${fixedWidth("172px")}
  }
`;

const rankAndPointStyle = css`
  ${flexColumn};
  align-items: flex-end;

  ${fixedWidth("66px")}
  gap: 2px;
  padding-top: 6px;
  padding-right: 8px;
  border-right: 1px solid #ccc;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    ${fixedWidth("40px")}
  }
`;

const rankLabelStyle = (theme: Theme) => css`
  color: ${theme.palette.primary.main};
  font-size: 12px;
  font-weight: 600;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    font-size: 10px;
  }
`;

const pointStyle = css`
  font-size: 12px;
  font-weight: 600;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    font-size: 14px;
  }
`;

const categoryNameStyle = css`
  width: 130px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-left: 8px;
  font-size: 16px;
  font-weight: 600;

  @media (max-width: ${SP_MODE_WIDTH}px) {
    font-size: 12px;
  }
`;

export default PointReport;
